import {
  type PictureTypeData,
  type AcquireBookPictures,
  type AcquireBookZenbujiko,
  type AllMattersAnalyzeOptions,
} from "@/types/acquirebook";
import { type ReusePictures } from "@/features/acquireBook/utils/pictureRequest";
import {
  extractPicturesAndZenbujikoOptions,
  buildCommonAcquireFields,
  type CommonAcquireFields,
} from "@/features/propertyCodeAcquisition/utils/newAcquireRequest";

// 物件コード起点のリユース実行 payload（OpenAPI 正本 PropertyCodePictureRequestReuseAcquireRequest）を組み立てる純粋関数。
//
// - 所在/地番/家屋番号は送らない（物件コードでリクエストする）
// - 新規取得（newAcquireRequest）との差分は reuse_pictures を持つ点のみ
// - 取得方式ラジオの選択（usePictureCheckOptionsManager.getReusePictures()）を
//   「新規（getNewPicture=true）」と「リユース（reuseDetailId 非null）」に振り分け、
//   新規分を pictures に、リユース分を reuse_pictures に入れる（互いに素）

/** リユース対象1件（PropertyCodePictureRequestReuseAcquireRequest.reuse_pictures の要素・キャメルケース）。 */
export interface PropertyCodeReusePictureItem {
  /** 登記/図面種別 */
  picture: AcquireBookPictures;
  /** リユース確認で保持した picture_request_detail_id（非null・改変しない） */
  reuseDetailId: number;
  /** リユース不可時に新規取得するか */
  getNewPicture: boolean;
}

/** リユース実行リクエスト（PropertyCodePictureRequestReuseAcquireRequest 相当・キャメルケース）。 */
export interface PropertyCodeReuseAcquireRequest extends CommonAcquireFields {
  /** リユース指定リスト（空不可） */
  reusePictures: PropertyCodeReusePictureItem[];
  /** 同時新規取得分（空可） */
  pictures: AcquireBookPictures[];
  /** 全部事項オプションリスト（新規 ZENBUJIKO がある場合のみ・空可） */
  zenbujikoOptions: AcquireBookZenbujiko[];
}

interface BuildPropertyCodeReuseAcquireRequestParams {
  /** 取得対象チェックボックスの選択状態（新規分の pictures/zenbujiko_options 抽出元） */
  requestOptions: PictureTypeData;
  /** getReusePictures() の戻り（新規=getNewPicture true / リユース=reuseDetailId 非null） */
  reusePictures: ReusePictures[];
  withOwnerAnalyze: boolean;
  withAllMattersAnalyze: boolean;
  identificationName: string;
  allMattersAnalyzeOptions?: AllMattersAnalyzeOptions;
}

/** getReusePictures() の戻りから、リユース選択分（reuse_detail_id 非null）だけを取り出す。 */
export const pickReuseSelections = (
  reusePictures: ReusePictures[]
): PropertyCodeReusePictureItem[] =>
  reusePictures
    // type predicate で reuseDetailId が number に絞り込まれるため as キャスト不要。
    .filter(
      (r): r is ReusePictures & { reuseDetailId: number } =>
        !r.getNewPicture && r.reuseDetailId !== null
    )
    .map((r) => ({
      picture: r.picture,
      reuseDetailId: r.reuseDetailId,
      getNewPicture: r.getNewPicture,
    }));

/** getReusePictures() の戻りから、新規取得分（getNewPicture=true）の登記種別集合を取り出す。 */
export const pickNewPictureSet = (
  reusePictures: ReusePictures[]
): Set<AcquireBookPictures> =>
  new Set<AcquireBookPictures>(
    reusePictures.filter((r) => r.getNewPicture).map((r) => r.picture)
  );

/**
 * リユース選択の整合検証。
 * チェック済みの種別のうち、リユース実行 payload（reuse_pictures ∪ 新規 pictures）でカバーされない
 * 種別を返す。返り値が空でなければ「黙って落ちる」選択があることを意味する。
 * 取得方式が未確定（getNewPicture=false かつ reuseDetailId=null）の種別はどちらにも振り分けられず欠落する。
 */
export const findUncoveredReusePictures = (params: {
  requestOptions: PictureTypeData;
  reusePictures: ReusePictures[];
}): AcquireBookPictures[] => {
  // 画面でチェックされている種別（送信されるべき種別）。
  const { pictures: checkedPictures } = extractPicturesAndZenbujikoOptions(
    params.requestOptions
  );
  // payload に実際に乗る種別＝リユース分 ∪ 新規分。
  const reuseItems = pickReuseSelections(params.reusePictures);
  const newPictureSet = pickNewPictureSet(params.reusePictures);
  const covered = new Set<AcquireBookPictures>([
    ...reuseItems.map((r) => r.picture),
    ...checkedPictures.filter((p) => newPictureSet.has(p)),
  ]);
  return checkedPictures.filter((p) => !covered.has(p));
};

/**
 * リユース実行 payload（PropertyCodePictureRequestReuseAcquireRequest）を組み立てる。
 * 新規分（getNewPicture=true）は pictures へ、リユース分は reuse_pictures へ振り分ける。
 * zenbujiko_options は新規取得対象に全部事項が含まれる場合のみ付与する。
 */
export const buildPropertyCodeReuseAcquireRequest = (
  params: BuildPropertyCodeReuseAcquireRequestParams
): PropertyCodeReuseAcquireRequest => {
  const reuseItems = pickReuseSelections(params.reusePictures);

  // 新規取得分（取得方式=新規）の登記種別集合。
  const newPictureSet = pickNewPictureSet(params.reusePictures);

  // 全選択から「新規分」だけに絞り込む（リユース分は reuse_pictures 側で送る）。
  const { pictures: allPictures, zenbujikoOptions } =
    extractPicturesAndZenbujikoOptions(params.requestOptions);
  const newPictures = allPictures.filter((p) => newPictureSet.has(p));
  // 全部事項オプションは新規取得対象に全部事項が含まれる場合のみ送る。
  const payloadZenbujikoOptions = newPictureSet.has("ZENBUJIKO")
    ? zenbujikoOptions
    : [];

  return {
    reusePictures: reuseItems,
    pictures: newPictures,
    zenbujikoOptions: payloadZenbujikoOptions,
    ...buildCommonAcquireFields(params),
  };
};
